import { AssistantType, VendorModel } from '@vendasta/protos/ai_assistants/v1alpha1';
import { AiAssistant } from './interfaces/ai-assistant.interface';
import { DEFAULT_AVATAR_URLS } from './avatar-urls';

export const VENDASTA_PARTNER = 'VA';

export const SMSConnectionID = 'CONNECTION-sms';

export const VoiceConnectionID = 'CONNECTION-voice';

// Personal email has no fixed connection ID: connections are per-mailbox, keyed by
// the Platform Integrations connection ID (see RFC appendix-a-per-mailbox-connections).
// Use ConnectionType.PersonalEmail + the per-mailbox connection id instead.

export const knowledgeAppType = 'AIAssistant';

export const SYSTEM_ASSISTANT_ID = 'ASSISTANT-system';
export const CHAT_RECEPTIONIST_ASSISTANT_ID = 'ASSISTANT-chat-receptionist';
export const DEFAULT_CHAT_RECEPTIONIST_AVATAR_SVG_ICON = 'ai-chat-receptionist';
export const VENDASTA_AI_AVATAR_SVG_ICON = 'vendasta-ai';
export const DEFAULT_AVATAR_SVG_ICON = 'galaxy-ai-icon-with-background';

export const CONTENT_CREATOR = 'content_creator';
export const WEBRTC_VOICE = 'webrtc_voice';
/** Gates OpenAI Live as a selectable speech provider. Voice falls back to Realtime without it. */
export const VOICE_OPENAI_LIVE = 'voice_openai_live';
export const AI_ASSISTANT_GENERATOR = 'ai_assistant_generator';
/** Gates personal-email on-demand AI drafting (AI Draft Replies RFC, Sprint 1). */
export const AI_DRAFT_PREVIEW_ENABLED = 'ai_draft_preview_enabled';
/** Gates the Success evaluators card on the assistant settings page (DALE-1446). */
export const AI_SUCCESS_EVALUATORS = 'ai_success_evaluators';
/** Gates authoring your own success evaluators from that card (DALE-1578). */
export const AI_CUSTOM_SUCCESS_EVALUATORS = 'custom_ai_success_evaluators';
/** Gates the Guardrails panel on the assistant settings page (DALE-1555). */
export const AI_ASSISTANT_GUARDRAILS = 'ai_assistant_guardrails';
/**
 * Gates version history on the assistant settings page (DALE-1569): the History button, and the version message
 * collected before publishing. Off, a save is one button press as it was before.
 */
export const AI_ASSISTANT_VERSIONING = 'ai_assistant_versioning';
/**
 * Gates the redesigned assistant list tiles and the summary page that sits between the list and the editor,
 * which takes over triggers, automations, knowledge and jobs to be done from the editor (DALE-1676).
 * Read alongside `AI_ASSISTANT_VERSIONING`: the tiles and the summary page report version state.
 */
export const ASSISTANT_CONFIGURATION_V2 = 'assistant_configuration_v2';
export const TELKOM_VOICE_AGENT_DEMO = 'telkom_voice_agent_demo';

export const TELKOM_VOICE_AGENT_DEMO_AGID = 'AG-XFPHFS4BFX';
export const TELKOM_VOICE_AGENT_DEMO_PHONE_NUMBER = '+27123212552';

// Default voice provider and voice settings
export const DEFAULT_VOICE_PROVIDER = VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME;

export const DEFAULT_PROVIDER_VOICES = new Map<VendorModel, string>([
  [VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME, 'marin'],
  [VendorModel.VENDOR_MODEL_OPEN_AI_LIVE, 'marin'],
  [VendorModel.VENDOR_MODEL_OPEN_AI, 'ash'],
  [VendorModel.VENDOR_MODEL_DEEPGRAM, 'aura-2-thalia-en'],
  [VendorModel.VENDOR_MODEL_ELEVEN_LABS, '9BWtsMINqrJLrRacOk9x'],
]);

export const WEBCHAT_WIDGET_CONNECTION_TYPE = 'WebchatWidget';
export const SNAPSHOT_WIDGET_CONNECTION_TYPE = 'SnapshotWidget';

export const ASSISTANT_ID_CHAT_RECEPTIONIST = 'ASSISTANT-chat-receptionist';
export const ASSISTANT_ID_VOICE_RECEPTIONIST = 'ASSISTANT-voice-receptionist';
export const ASSISTANT_ID_CONTENT_CREATOR = 'ASSISTANT-content-creator';
export const ASSISTANT_ID_REPUTATION_SPECIALIST = 'ASSISTANT-reputation-specialist';
export const ASSISTANT_ID_AI_SEARCH_EXPERT = 'ASSISTANT-AI-SEARCH-EXPERT';
export const ASSISTANT_ID_SALES_COACH = 'ASSISTANT-sales-coach';
export const ASSISTANT_ID_MARKETER = 'ASSISTANT-marketer';
export const ASSISTANT_ID_BLOGGER = 'ASSISTANT-blogger';
/**
 * Well-known id string for the in-app Business App assistant
 * (AssistantType ASSISTANT_TYPE_BUSINESS_APP_ASSISTANT), combined with the owning partner
 * namespace to form the full assistant key. Must match the id the backend provisions.
 */
export const ASSISTANT_ID_BUSINESS_APP = 'ASSISTANT-business-app';
export const CUSTOM_ASSISTANT_PLACEHOLDER_ID = 'ASSISTANT-custom-placeholder';

export const PROFILE_OPTIMIZATION_AUTOMATION_TEMPLATE_ID =
  'TemplateManagedAutomation-ASSISTANT-ai-search-specialist-profile-optimization';

// Default avatar (profile picture) per assistant role, hosted on the media service
// so every surface can request a size rather than a fixed file. This is the shared
// source of truth for both business-center-client and partner-center-client. The
// same files and per-type mapping are mirrored by the ai-assistants backend in
// internal/assistant/avatar/resolver.go — keep the two in sync.
export const DEFAULT_ASSISTANT_AVATAR_URL: Record<string, string> = {
  [ASSISTANT_ID_CHAT_RECEPTIONIST]: DEFAULT_AVATAR_URLS['ai-receptionist.jpg'],
  [ASSISTANT_ID_VOICE_RECEPTIONIST]: DEFAULT_AVATAR_URLS['ai-receptionist.jpg'],
  [ASSISTANT_ID_CONTENT_CREATOR]: DEFAULT_AVATAR_URLS['ai-content.jpg'],
  [ASSISTANT_ID_BLOGGER]: DEFAULT_AVATAR_URLS['ai-blogger.jpg'],
  [ASSISTANT_ID_REPUTATION_SPECIALIST]: DEFAULT_AVATAR_URLS['ai-reputation.jpg'],
  [ASSISTANT_ID_SALES_COACH]: DEFAULT_AVATAR_URLS['ai-sales-assistant.jpg'],
  [ASSISTANT_ID_AI_SEARCH_EXPERT]: DEFAULT_AVATAR_URLS['ai-search.jpg'],
  [CUSTOM_ASSISTANT_PLACEHOLDER_ID]: DEFAULT_AVATAR_URLS['ai-custom.png'],
};

/**
 * The AI employees a business sees on the Business App AI Workforce page, before any
 * provisioned assistants are merged in. Shared so Partner Center can derive the list of
 * employees a partner is allowed to hide from their customers.
 *
 * Not to be confused with `AI_DEFAULT_PARTNERS_ASSISTANT_WORKFORCE` in partner-center-client,
 * which describes the partner's own AI employees.
 */
export const AI_DEFAULT_BUSINESS_ASSISTANT_WORKFORCE: AiAssistant[] = [
  {
    assistant: {
      id: ASSISTANT_ID_CHAT_RECEPTIONIST,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CHAT_RECEPTIONIST.TITLE',
      type: AssistantType.ASSISTANT_TYPE_INBOX,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CHAT_RECEPTIONIST.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_CHAT_RECEPTIONIST],
      gradientColor: '#22C0CA',
    },
    isDefault: true,
    showConfigButtonOnDefault: true,
    autonomyRating: 3,
    order: 1,
  },
  {
    assistant: {
      id: ASSISTANT_ID_VOICE_RECEPTIONIST,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.VOICE_RECEPTIONIST.TITLE',
      type: AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.VOICE_RECEPTIONIST.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_VOICE_RECEPTIONIST],
      gradientColor: '#A16EEF',
    },
    isDefault: true,
    showConfigButtonOnDefault: true,
    autonomyRating: 3,
    order: 2,
  },
  {
    assistant: {
      id: ASSISTANT_ID_CONTENT_CREATOR,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CONTENT_CREATOR.TITLE',
      type: AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CONTENT_CREATOR.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_CONTENT_CREATOR],
      gradientColor: '#0097EC',
    },
    isDefault: true,
    autonomyRating: 3,
    order: 3,
    showManageTriggersButton: false,
  },
  {
    assistant: {
      id: ASSISTANT_ID_BLOGGER,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.BLOG_WRITER.TITLE',
      type: AssistantType.ASSISTANT_TYPE_BLOGGER,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.BLOG_WRITER.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_BLOGGER],
      gradientColor: '#e358d8',
    },
    isDefault: true,
    autonomyRating: 3,
    order: 4,
    showManageTriggersButton: false,
  },
  {
    assistant: {
      id: ASSISTANT_ID_REPUTATION_SPECIALIST,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.REPUTATION_SPECIALIST.TITLE',
      type: AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.REPUTATION_SPECIALIST.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_REPUTATION_SPECIALIST],
      gradientColor: '#F7CB16',
    },
    isDefault: true,
    autonomyRating: 3,
    order: 5,
  },
  {
    assistant: {
      id: ASSISTANT_ID_SALES_COACH,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.SALES_COACH.TITLE_V2',
      type: AssistantType.ASSISTANT_TYPE_SALES_COACH,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.SALES_COACH.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_SALES_COACH],
      gradientColor: '#A16EEF',
    },
    isDefault: true,
    order: 6,
  },
  {
    assistant: {
      id: ASSISTANT_ID_MARKETER,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.MARKETER.TITLE',
      type: AssistantType.ASSISTANT_TYPE_MARKETER,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.MARKETER.DESCRIPTION',
    decoration: {
      gradientColor: '#9066F7',
    },
    isDefault: true,
    order: 7,
  },
  {
    assistant: {
      id: ASSISTANT_ID_AI_SEARCH_EXPERT,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.SEARCH_EXPERT.TITLE',
      type: AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.SEARCH_EXPERT.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[ASSISTANT_ID_AI_SEARCH_EXPERT],
      gradientColor: '#077a3f',
    },
    isDefault: true,
    isBeta: true,
    autonomyRating: 3,
    order: 8,
  },
  {
    assistant: {
      id: CUSTOM_ASSISTANT_PLACEHOLDER_ID,
      name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.TITLE',
      type: AssistantType.ASSISTANT_TYPE_CUSTOM,
    },
    descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.DESCRIPTION',
    decoration: {
      avatarIconUrl: DEFAULT_ASSISTANT_AVATAR_URL[CUSTOM_ASSISTANT_PLACEHOLDER_ID],
      gradientColor: '#9066F7',
    },
    isDefault: true,
    order: 9,
  },
];

export const disabled_card_colour = '#e0e0e0';
export const DEFAULT_CONNECTION_PREFIX = 'default-';

// Banner attribution (Manage Reviews → Reputation Specialist config)
export const AI_AUTO_RESPONSE_BANNER_SOURCE_QUERY_KEY = 'aiAutoResponseBannerSource';
export const AI_AUTO_RESPONSE_BANNER_SOURCE_VALUE = 'ba-manage-reviews';

// `?successEvaluators=true` shows the Jobs to be done panel without the feature
// flag, so it can be demoed on a partner the flag is off for. Read when the page
// loads, so adding or removing it takes a reload. It only reveals the panel — the
// data behind it still comes from the API.
/** The label the workforce providers put on a "try this assistant out" CTA, wherever it is offered. */
export const TRY_IT_CTA_LABEL = 'AI_ASSISTANT.WORKFORCE.TRY_IT';

export const AI_SUCCESS_EVALUATORS_QUERY_KEY = 'successEvaluators';

// `?customSuccessEvaluators=true` does the same for the "Add job" button, and
// reveals the panel too so the authoring flow demos with one param. The feature
// flags behind the two are independent of each other.
export const AI_CUSTOM_SUCCESS_EVALUATORS_QUERY_KEY = 'customSuccessEvaluators';

export const WEBCHAT_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/webchat-icon.svg';
export const SMS_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/phone.svg';
export const VOICE_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/old-phone.svg';

export const DEFAULT_ASSISTANT_NAMES = new Map<AssistantType, string>([
  [AssistantType.ASSISTANT_TYPE_UNSPECIFIED, 'AI Assistant'],
  [AssistantType.ASSISTANT_TYPE_CUSTOM, 'Custom Employee'],
  [AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST, 'Voice Receptionist'],
  [AssistantType.ASSISTANT_TYPE_SALES_COACH, 'Sales Coach'],
  [AssistantType.ASSISTANT_TYPE_SYSTEM, 'System'],
  [AssistantType.ASSISTANT_TYPE_INBOX, 'Chat Receptionist'],
  [AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT, 'Review Specialist'],
  [AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING, 'SEO Analyst'],
  [AssistantType.ASSISTANT_TYPE_MARKETER, 'AI Marketer'],
  [AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT, 'AI Search Specialist'],
  [AssistantType.ASSISTANT_TYPE_BUSINESS_APP_ASSISTANT, 'Business App Assistant'],
]);

// Default global goals
export const DEFAULT_LEAD_CAPTURE_GOAL_ID = 'DefaultLeadCapture';
export const DEFAULT_MEETING_BOOKING_GOAL_ID = 'BookAppointmentsWithBookMeNow';
export const ADDITIONAL_INSTRUCTIONS_GOAL_ID = 'AdditionalInstructions';
export const DEFAULT_VOICE_LEAD_CAPTURE_GOAL_ID = 'DefaultVoiceLeadCapture';
export const DEFAULT_SHOPIFY_MCP_GOAL_ID = 'DefaultShopifyMCP';
export const HUBSPOT_MCP_GOAL_ID = 'Hubspot_MCP';
export const DEFAULT_OCTOTABLE_GOAL_ID = 'OctoTableReservationManagement';
export const DEFAULT_DUDA_BOOKINGS_GOAL_ID = 'DudaBookingsAppointmentScheduling';
export const DEFAULT_TRANSFER_CALL_GOAL_ID = 'TransferCall';
export const DEFAULT_ONCALL_DISPATCH_GOAL_ID = 'OnCallDispatch';
export const DEFAULT_CRM_UPDATE_FROM_MEETING_GOAL_ID = 'CRM_UpdateFromMeeting';
export const DEFAULT_CRM_TALK_WITH_YOUR_DATA_GOAL_ID = 'CRM_TalkWithYourData';
export const DEFAULT_SNAPSHOT_REPORT_INSIGHTS_GOAL_ID = 'SnapshotReportInsights';
export const DEFAULT_CUSTOM_PERSONALITY_GOAL_ID = 'DefaultCustomPersonality';
export const DEFAULT_MULTIPLE_MEETING_BOOKING_GOAL_ID = 'BookMultipleAppointmentsWithBookMeNow';
export const DEFAULT_VOICE_MULTIPLE_MEETING_BOOKING_GOAL_ID = 'BookMultipleVoiceAppointmentsWithBookMeNow';

export const CONTEXTUAL_PARAMETER_CAPABILITIES: Record<string, string[]> = {
  [DEFAULT_CRM_TALK_WITH_YOUR_DATA_GOAL_ID]: ['crm_object_id', 'crm_object_type'],
  [DEFAULT_SNAPSHOT_REPORT_INSIGHTS_GOAL_ID]: ['snapshot_id'],
};

export enum PlatformLocation {
  PLATFORM_LOCATION_UNDEFINED = 0,
  PLATFORM_LOCATION_BUSINESS_APP = 1,
  PLATFORM_LOCATION_PARTNER_CENTER = 2,
  PLATFORM_LOCATION_ADMIN_CENTER = 3,
}

export enum GoalNamespaceType {
  Local = 'local',
  Global = 'global',
  System = 'system',
}

export const VOICE_SUPPORTED_REGIONS = ['US', 'CA', 'IT', 'AU', 'GB', 'DE'];
